/**
 * Project Name: Brainsie eCommerce v4
 * Component: Grid Layout 3 Column
 * Description: Three column grid layout with simple cards
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import ComponentRenderer from "../ComponentRenderer";

export default function GridLayout3Column({
  section,
  components,
  loading,
  error,
  pageData,
  darkMode,
  theme,
}) {
  // Get section settings
  const sectionSettings = section.Section_Settings
    ? JSON.parse(section.Section_Settings)
    : {};

  // Check if background image should be applied
  const hasBackgroundImage =
    section.Section_Media &&
    section.Section_Media !== "NA" &&
    section.Section_Media !== "" &&
    section.Section_Media !== null;

  // Helper function to get theme-aware text color
  const getTextColor = () => {
    if (sectionSettings.textColor) {
      return sectionSettings.textColor;
    }
    // When background image is present, default to white for better contrast
    if (hasBackgroundImage) {
      return "#ffffff";
    }
    return darkMode ? theme.palette.common.white : theme.palette.text.primary;
  };

  console.log("🟢 GRID LAYOUT 3 COLUMN RENDERING");

  return (
    <Box
      component="section"
      className="grid-layout-3-column"
      sx={{
        py: sectionSettings.paddingY || 8,
        px: sectionSettings.paddingX || 0,
        position: "relative",
        backgroundColor: hasBackgroundImage ? "transparent" : "transparent",
        color: getTextColor(),
        // Background image styling
        ...(hasBackgroundImage && {
          backgroundImage: `url(${process.env.BASEURL}${section.Section_Media})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "scroll",
        }),
        ...sectionSettings.customStyles,
      }}
    >
      {/* Background Overlay for better text readability */}
      {hasBackgroundImage && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor:
              sectionSettings.overlayColor || "rgba(0, 0, 0, 0.5)",
            zIndex: 1,
          }}
        />
      )}
      <Container
        maxWidth={sectionSettings.maxWidth || "lg"}
        sx={{
          position: "relative",
          zIndex: 2, // Ensure content appears above overlay
        }}
      >
        {/* Section Header */}
        {section.Section_Heading && section.Section_Heading !== "NA" && (
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                mb: 2,
                color: sectionSettings.headingColor || getTextColor(),
                fontWeight: 600,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                lineHeight: 1.2,
              }}
            >
              {section.Section_Heading}
            </Typography>

            {section.Section_Sub_Heading &&
              section.Section_Sub_Heading !== "NA" && (
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{
                    mb: 2,
                    color: sectionSettings.subHeadingColor || getTextColor(),
                    opacity: 0.9,
                    fontWeight: 400,
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                  }}
                >
                  {section.Section_Sub_Heading}
                </Typography>
              )}

            {section.Section_Sub_Text && section.Section_Sub_Text !== "NA" && (
              <Typography
                variant="body1"
                sx={{
                  color: sectionSettings.bodyColor || getTextColor(),
                  opacity: 0.8,
                  lineHeight: 1.6,
                  fontSize: "1.1rem",
                  maxWidth: "600px",
                  mx: "auto",
                }}
              >
                {section.Section_Sub_Text}
              </Typography>
            )}
          </Box>
        )}

        {/* Grid Layout for Cards */}
        {loading ? (
          <Grid container spacing={4} justifyContent="center">
            {[1, 2, 3].map((item) => (
              <Grid key={item} item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    backgroundColor: hasBackgroundImage
                      ? "rgba(255, 255, 255, 0.9)"
                      : darkMode
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    p: 3,
                    height: "280px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* Card skeleton content */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "rgba(128, 128, 128, 0.2)",
                      mb: 2,
                    }}
                  />
                  <Box
                    sx={{
                      width: "80%",
                      height: 20,
                      backgroundColor: "rgba(128, 128, 128, 0.2)",
                      borderRadius: 1,
                      mb: 1,
                    }}
                  />
                  <Box
                    sx={{
                      width: "100%",
                      height: 40,
                      backgroundColor: "rgba(128, 128, 128, 0.1)",
                      borderRadius: 1,
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : error ? (
          <Box
            sx={{
              textAlign: "center",
              color: theme.palette.error.main,
              backgroundColor: hasBackgroundImage
                ? "rgba(255, 255, 255, 0.9)"
                : "transparent",
              borderRadius: hasBackgroundImage ? "8px" : 0,
              p: hasBackgroundImage ? 3 : 0,
            }}
          >
            Error loading components: {error.message}
          </Box>
        ) : (
          <Grid container spacing={4} justifyContent="center">
            {components
              ?.filter(
                (component) =>
                  component.Component_Name === "Simple Card" ||
                  component.Component_Name === "Icon Card" ||
                  component.Component_Name === "Blog Card"
              )
              ?.map((component, index) => (
                <Grid
                  key={component.Component_Id}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <ComponentRenderer
                    component={component}
                    section={section}
                    darkMode={darkMode}
                    theme={theme}
                    gridIndex={index + 1}
                  />
                </Grid>
              ))}
          </Grid>
        )}

        {/* Render any other components not specifically handled */}
        {components
          ?.filter(
            (component) =>
              component.Component_Name !== "Simple Card" &&
              component.Component_Name !== "Icon Card" &&
              component.Component_Name !== "Blog Card"
          )
          ?.map((component) => (
            <Box key={component.Component_Id} sx={{ mt: 4 }}>
              <ComponentRenderer
                component={component}
                section={section}
                darkMode={darkMode}
                theme={theme}
              />
            </Box>
          ))}
      </Container>
    </Box>
  );
}
